import { User } from 'lucide-react';
import { getIconByName } from '../../utils/iconUtils';
import { useTranslation } from '../../context/LanguageContext';

/**
 * Normalise un type de profil, quelle que soit la casse rendue.
 *
 * `queryToArray` met les clés en minuscules, mais la casse varie selon le
 * chemin de sérialisation : les trois formes sont lues.
 *
 * Rend null quand il n'y a rien à afficher — une pastille sans code serait
 * un rectangle vide, plus déroutant qu'une absence.
 *
 * @param {object|null} source - Une personne, ou un type de profil
 * @returns {{code: string, couleurFond: string, couleurTexte: string, icone: string}|null}
 */
export function lireTypeProfil(source) {
    if (!source) return null;

    const brut = source.profileTypeCode ?? source.profiletypecode ?? source.PROFILETYPECODE
              ?? source.code ?? source.CODE ?? '';
    const code = String(brut).trim().toUpperCase();
    if (!code) return null;

    return {
        code,
        couleurFond: source.badgeBgColor ?? source.badgebgcolor ?? source.BADGEBGCOLOR ?? '',
        couleurTexte: source.badgeTextColor ?? source.badgetextcolor ?? source.BADGETEXTCOLOR ?? '',
        icone: source.icon ?? source.ICON ?? ''
    };
}

/**
 * La pastille du type de profil.
 *
 * Savoir à qui l'on a affaire est le concept même de Cabaza : un étudiant,
 * un artisan et un pensionné n'attendent pas la même chose, et les autres
 * membres ont besoin de le savoir.
 *
 * DEUX FORMES, et ce n'est pas un détail : dans une liste longue, une
 * pastille complète par ligne encombre au point de noyer les noms. La forme
 * compacte n'affiche que l'icône, avec le libellé en infobulle.
 *
 * Un type sans couleur reste lisible : le gris neutre vaut mieux qu'une
 * pastille invisible ou qu'une couleur inventée au rendu.
 *
 * @param {object} type - Une personne ou un type, tel que rendu par l'API
 * @param {boolean} compact - Forme réduite, pour les listes
 */
const ProfileTypeBadge = ({ type, compact = false, className = '' }) => {
    const { t } = useTranslation();

    const donnees = lireTypeProfil(type);
    if (!donnees) return null;

    const libelle = t.profileTypes?.[donnees.code.toLowerCase()] || donnees.code;
    const Icone = getIconByName(donnees.icone, User);

    // Les couleurs viennent de la base, jamais d'une classe Tailwind : une
    // classe construite au rendu ne serait pas générée au build.
    const style = {};
    if (donnees.couleurFond) style.backgroundColor = donnees.couleurFond;
    if (donnees.couleurTexte) style.color = donnees.couleurTexte;

    const neutre = donnees.couleurFond ? '' : 'bg-gray-100 text-gray-700';

    if (compact) {
        return (
            <span
                title={libelle}
                aria-label={libelle}
                style={style}
                className={`inline-flex items-center justify-center w-5 h-5 rounded ${neutre} ${className}`}
                data-testid="pastille-type-compacte"
            >
                <Icone className="w-3 h-3" aria-hidden="true" />
            </span>
        );
    }

    return (
        <span
            style={style}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${neutre} ${className}`}
            data-testid="pastille-type"
        >
            <Icone className="w-3 h-3" aria-hidden="true" />
            {libelle}
        </span>
    );
};

export default ProfileTypeBadge;
